import {ActionList, ActionMenu, Button, Spinner} from '@primer/react'
import {CopilotIcon, TriangleDownIcon} from '@primer/octicons-react'
import {useCallback, useEffect, useEffectEvent, useRef, useState} from 'react'
import {announce} from '@github-ui/aria-live'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import styles from './ModelPicker.module.css'

export interface ModelOption {
  id: string
  name: string
  icon?: string
  multiplier?: number
}

interface ModelsResponse {
  models: ModelOption[]
  current_model?: string
}

export interface ModelPickerProps {
  modelsUrl: string
  selectedModel?: ModelOption | null
  onModelChange?: (modelId: string, model: ModelOption) => void
}

const FALLBACK_AUTO_MODEL: ModelOption = {id: 'auto', name: 'Auto', multiplier: 1}

export function ModelPicker({modelsUrl, selectedModel, onModelChange}: ModelPickerProps) {
  const [models, setModels] = useState<ModelOption[]>([FALLBACK_AUTO_MODEL])
  const [uncontrolledSelectedModelId, setUncontrolledSelectedModelId] = useState(FALLBACK_AUTO_MODEL.id)
  const [isLoading, setIsLoading] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const announcementAnchorRef = useRef<HTMLSpanElement>(null)
  const pendingFallbackAnnouncementRef = useRef<{modelId: string; message: string} | null>(null)
  const isControlled = selectedModel !== undefined
  const resolvedSelectedModelId = isControlled
    ? (selectedModel?.id ?? FALLBACK_AUTO_MODEL.id)
    : uncontrolledSelectedModelId
  const announceFromPicker = useCallback((message: string) => {
    const anchor = announcementAnchorRef.current
    if (anchor) announce(message, {element: anchor})
  }, [])

  const handleModelsLoaded = useEffectEvent((data: ModelsResponse): string | null => {
    if (data.models.length === 0) {
      setModels([FALLBACK_AUTO_MODEL])
      if (!isControlled) setUncontrolledSelectedModelId(FALLBACK_AUTO_MODEL.id)
      if (resolvedSelectedModelId !== FALLBACK_AUTO_MODEL.id) {
        pendingFallbackAnnouncementRef.current = {
          modelId: FALLBACK_AUTO_MODEL.id,
          message: 'No model options were available. Model selection changed to Auto.',
        }
        onModelChange?.(FALLBACK_AUTO_MODEL.id, FALLBACK_AUTO_MODEL)
        return null
      }
      return 'No model options were available. Auto is still available.'
    }

    setModels(data.models)
    const defaultModel =
      data.models.find(model => model.id === data.current_model) ?? data.models[0] ?? FALLBACK_AUTO_MODEL

    if (!isControlled) {
      setUncontrolledSelectedModelId(defaultModel.id)
      onModelChange?.(defaultModel.id, defaultModel)
      return 'Model options loaded.'
    }

    if (selectedModel === null) {
      onModelChange?.(defaultModel.id, defaultModel)
      return 'Model options loaded.'
    }

    if (data.models.some(model => model.id === selectedModel.id)) {
      return 'Model options loaded.'
    } else {
      pendingFallbackAnnouncementRef.current = {
        modelId: defaultModel.id,
        message: `Model options loaded. Model selection changed to ${defaultModel.name} because the previous model is unavailable.`,
      }
      onModelChange?.(defaultModel.id, defaultModel)
      return null
    }
  })

  const handleModelsLoadFailure = useEffectEvent((): string | null => {
    setModels([FALLBACK_AUTO_MODEL])
    if (!isControlled) setUncontrolledSelectedModelId(FALLBACK_AUTO_MODEL.id)
    if (resolvedSelectedModelId !== FALLBACK_AUTO_MODEL.id) {
      pendingFallbackAnnouncementRef.current = {
        modelId: FALLBACK_AUTO_MODEL.id,
        message: 'Model options could not be loaded. Model selection changed to Auto.',
      }
      onModelChange?.(FALLBACK_AUTO_MODEL.id, FALLBACK_AUTO_MODEL)
      return null
    }
    return 'Model options could not be loaded. Auto is still available.'
  })

  const announceLoadResult = useEffectEvent((message: string | null) => {
    if (isOpen && message) announceFromPicker(message)
  })

  useEffect(() => {
    let ignore = false

    async function fetchModels() {
      setIsLoading(true)
      let loadAnnouncement: string | null = 'Model options could not be loaded. Auto is still available.'

      try {
        const response = await verifiedFetchJSON(modelsUrl)
        if (response.ok) {
          const data: ModelsResponse = await response.json()
          if (ignore) return
          loadAnnouncement = handleModelsLoaded(data)
        } else if (!ignore) {
          loadAnnouncement = handleModelsLoadFailure()
        }
      } catch {
        if (!ignore) loadAnnouncement = handleModelsLoadFailure()
      } finally {
        if (!ignore) {
          setIsLoading(false)
          announceLoadResult(loadAnnouncement)
        }
      }
    }

    void fetchModels()

    return () => {
      ignore = true
    }
  }, [modelsUrl])

  useEffect(() => {
    const pendingAnnouncement = pendingFallbackAnnouncementRef.current
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- Controlled fallbacks must be announced only after the parent commits the new selected value.
    if (!pendingAnnouncement || pendingAnnouncement.modelId !== resolvedSelectedModelId) return

    pendingFallbackAnnouncementRef.current = null
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler -- Announce only while the menu exposing the status remains open.
    if (isOpen) announceFromPicker(pendingAnnouncement.message)
  }, [announceFromPicker, isOpen, resolvedSelectedModelId])

  const handleSelect = useCallback(
    (model: ModelOption) => {
      pendingFallbackAnnouncementRef.current = null
      if (!isControlled) {
        setUncontrolledSelectedModelId(model.id)
      }
      onModelChange?.(model.id, model)
    },
    [isControlled, onModelChange],
  )

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setIsOpen(open)
      if (open && isLoading) announceFromPicker('Loading model options.')
    },
    [announceFromPicker, isLoading],
  )

  const availableSelectedModel = models.find(model => model.id === resolvedSelectedModelId)
  const displayName = availableSelectedModel?.name ?? selectedModel?.name ?? FALLBACK_AUTO_MODEL.name

  return (
    <ActionMenu open={isOpen} onOpenChange={handleOpenChange}>
      <ActionMenu.Anchor>
        <Button
          size="small"
          variant="invisible"
          className={styles.pickerButton}
          trailingVisual={TriangleDownIcon}
          aria-label={`Model: ${displayName}`}
        >
          <span ref={announcementAnchorRef}>Model: </span>
          <span data-testid="model-picker-selected">{displayName}</span>
        </Button>
      </ActionMenu.Anchor>
      <ActionMenu.Overlay width="medium">
        {isLoading ? (
          <div className={styles.spinnerContainer}>
            <Spinner size="small" srText={null} data-testid="model-picker-spinner" />
          </div>
        ) : (
          <ActionList selectionVariant="single">
            <ActionList.Group>
              <ActionList.GroupHeading>Models</ActionList.GroupHeading>
              {models.map(model => (
                <ActionList.Item
                  key={model.id}
                  selected={model.id === resolvedSelectedModelId}
                  onSelect={() => handleSelect(model)}
                >
                  {model.icon && model.id !== 'auto' ? (
                    <ActionList.LeadingVisual>
                      <img
                        data-testid={`model-picker-button-icon-${model.id}`}
                        src={model.icon}
                        alt=""
                        width={16}
                        height={16}
                        className={styles.modelIcon}
                      />
                    </ActionList.LeadingVisual>
                  ) : (
                    <ActionList.LeadingVisual>
                      <CopilotIcon size={16} />
                    </ActionList.LeadingVisual>
                  )}
                  {model.name}
                </ActionList.Item>
              ))}
            </ActionList.Group>
          </ActionList>
        )}
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}
